import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import {
  checkMarkButton,
  hourglassNotDone,
  noEntry,
  thinkingFace,
  warning,
} from '@/assets/images/emojis';
import { externalUrls } from '@/constants/urls';
import { routes } from '@/routes';
import { useEmailLinkValidation } from '../../hooks/useEmailLinkValidation';
import type { EmailLinkValidationStatus } from '../../types/email';
import styles from './EmailLinkValidationPage.module.scss';

const FLOW_QUERY_PARAM = 'flow';
const WEB_FLOW = 'web';

// Shown instead when the user gets to choose, since nothing is taking them
// anywhere.
const WEB_CHOICE_DESCRIPTION =
  'Continuă în aplicația Volt sau mergi mai departe aici, în web.';

type ResolvedStatus = Exclude<EmailLinkValidationStatus, 'idle' | 'pending'>;

interface StatusContent {
  emoji: string;
  emojiAlt: string;
  title: string;
  description: string;
}

// `invalid_code` covers expired, already used and unknown links, so the copy
// cannot claim which of the three happened.
const contentByStatus: Record<ResolvedStatus, StatusContent> = {
  success: {
    emoji: checkMarkButton,
    emojiAlt: 'Check mark emoji',
    title: 'Adresa de email\neste confirmată',
    description: 'Te ducem în aplicația Volt ca să continui de acolo.',
  },
  invalidLink: {
    emoji: noEntry,
    emojiAlt: 'No entry emoji',
    title: 'Linkul nu mai\neste valid',
    description:
      'Acest link de confirmare nu mai poate fi folosit. Cere un email nou de confirmare din aplicația Volt.',
  },
  limitExceeded: {
    emoji: hourglassNotDone,
    emojiAlt: 'Hourglass emoji',
    title: 'Prea multe\nîncercări',
    description:
      'Ai ajuns la limita de încercări. Te rugăm să încerci din nou mai târziu.',
  },
  notValidatable: {
    emoji: thinkingFace,
    emojiAlt: 'Thinking face emoji',
    title: 'Nu putem confirma\naceastă adresă',
    description:
      'Este posibil ca adresa să fie deja confirmată sau să fi fost schimbată între timp. Verifică starea ei în aplicația Volt.',
  },
  failed: {
    emoji: warning,
    emojiAlt: 'Warning emoji',
    title: 'Ceva nu a\nfuncționat',
    description:
      'Întâmpinăm o problemă tehnică. Te rugăm să încerci din nou. Dacă problema persistă, contactează echipa de suport.',
  },
};

export function EmailLinkValidationPage() {
  const { status, validate } = useEmailLinkValidation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // `?flow=web` on the link turns this into a choice screen. The same param is
  // already picked up by flowOverrideService at boot, so choosing web here also
  // keeps the user out of the app-redirect experiment after they sign in.
  const canContinueInWeb = searchParams.get(FLOW_QUERY_PARAM) === WEB_FLOW;

  const openApp = () => {
    window.location.href = externalUrls.volt.appDownload;
  };

  useEffect(() => {
    void validate();
  }, [validate]);

  // Without `?flow=web` this page is a pass-through to the app: there is
  // nothing for the user to do, so leave as soon as the email is confirmed.
  useEffect(() => {
    if (status !== 'success' || canContinueInWeb) {
      return;
    }

    openApp();
  }, [status, canContinueInWeb]);

  if (status === 'idle' || status === 'pending') {
    return <PageLoader />;
  }

  const offersWebChoice = status === 'success' && canContinueInWeb;
  const content = offersWebChoice
    ? { ...contentByStatus.success, description: WEB_CHOICE_DESCRIPTION }
    : contentByStatus[status];

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <img src={content.emoji} alt={content.emojiAlt} className={styles.emoji} />
        <div className={styles.titleSection}>
          <h1 className={styles.title}>{content.title}</h1>
          <p className={styles.description}>{content.description}</p>
        </div>
      </div>

      <div className={styles.actions}>
        {status === 'failed' ? (
          <Button
            trackingId="email_link_validation_retry"
            className={styles.button}
            text="Încearcă din nou"
            rightIcon={<Icon name="refresh" />}
            onClick={validate}
          />
        ) : (
          <>
            <Button
              trackingId="email_link_validation_open_app"
              className={styles.button}
              text="Deschide aplicația Volt"
              rightIcon={<Icon name="arrow-right" />}
              onClick={openApp}
            />

            {offersWebChoice && (
              <Button
                trackingId="email_link_validation_continue_web"
                variant="outlined"
                className={styles.button}
                text="Continuă în web"
                onClick={() => navigate(routes.wizard.base)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default EmailLinkValidationPage;
